import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const DEFAULT_QUOTE_FILTER_OPTIONS = ["All", "USDT", "BTC", "USDC", "ETH", "BNB"];

function useIsMarketMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767.98px)").matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767.98px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    if (mq.addEventListener) {
      mq.addEventListener("change", apply);
      return () => mq.removeEventListener("change", apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, []);

  return isMobile;
}

/**
 * @param {string} [value] — "All" or quote/base symbol (uppercase in UI)
 * @param {function} [onValueChange] — (next: string) => void. When set, the select is fully controlled.
 * @param {string[]} [options] — list including "All" first is recommended; defaults to DEFAULT_QUOTE_FILTER_OPTIONS
 */
export default function MarketQuoteSelect({ value, onValueChange, options: optionsProp }) {
  const [open, setOpen] = useState(false);
  const [uncontrolled, setUncontrolled] = useState("All");
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const [pendingValue, setPendingValue] = useState("All");
  const rootRef = useRef(null);

  const isMobile = useIsMarketMobile();

  const options = useMemo(() => {
    if (Array.isArray(optionsProp) && optionsProp.length > 0) return optionsProp;
    return DEFAULT_QUOTE_FILTER_OPTIONS;
  }, [optionsProp]);

  const isControlled = typeof onValueChange === "function";
  const selected = isControlled ? (value == null || value === "" ? "All" : value) : uncontrolled;

  useEffect(() => {
    if (mobileModalOpen) setPendingValue(selected);
  }, [mobileModalOpen, selected]);

  useEffect(() => {
    if (!isMobile && mobileModalOpen) setMobileModalOpen(false);
  }, [isMobile, mobileModalOpen]);

  useEffect(() => {
    if (!mobileModalOpen || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileModalOpen, isMobile]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    if (!open && !mobileModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setMobileModalOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, mobileModalOpen]);

  const applyValue = useCallback(
    (next) => {
      if (isControlled) onValueChange(next);
      else setUncontrolled(next);
    },
    [isControlled, onValueChange]
  );

  const confirmMobile = useCallback(() => {
    applyValue(pendingValue);
    setMobileModalOpen(false);
  }, [applyValue, pendingValue]);

  const mobileModal =
    mobileModalOpen && isMobile && typeof document !== "undefined"
      ? createPortal(
          <div
            className="market-quote-select-mobile-overlay"
            role="presentation"
            onClick={() => setMobileModalOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="market-quote-select-mobile-title"
              className="market-quote-select-mobile-sheet"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="market-quote-select-mobile-sheet__head">
                <h3 id="market-quote-select-mobile-title" className="market-quote-select-mobile-sheet__title">
                  All
                </h3>
                <button
                  type="button"
                  className="market-quote-select-mobile-sheet__close"
                  aria-label="Close"
                  onClick={() => setMobileModalOpen(false)}
                >
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
              </div>
              <ul className="market-quote-select-mobile-list" role="listbox">
                {options.map((opt) => (
                  <li key={String(opt)} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={pendingValue === opt}
                      className={`market-quote-select-mobile-option${pendingValue === opt ? " is-selected" : ""}`}
                      onClick={() => setPendingValue(opt)}
                    >
                      <span>{opt}</span>
                      {pendingValue === opt ? (
                        <span className="market-quote-select-mobile-option__check" aria-hidden="true">
                          <i className="ri-check-line" />
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="market-quote-select-mobile-sheet__confirm" onClick={confirmMobile}>
                Confirm
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="market-quote-select" ref={rootRef}>
      <button
        type="button"
        className={`market-quote-select__trigger${open ? " is-open" : ""} desktop-view`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="market-quote-select__label">{selected}</span>
        <i
          className={`market-quote-select__chev ri-arrow-${open ? "up" : "down"}-s-line`}
          aria-hidden="true"
        />
      </button>

      <button
        type="button"
        className="mobile-view market_crypto_select_currency"
        onClick={() => setMobileModalOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={mobileModalOpen}
      >
        {/* <span className="market_crypto_select_currency__label">{selected}</span> */}
        <i className="ri-more-2-fill" aria-hidden="true" />
      </button>

      {open ? (
        <ul className="market-quote-select__panel" role="listbox">
          {options.map((opt) => (
            <li key={String(opt)} role="presentation">
              <button
                type="button"
                className="market-quote-select__option"
                role="option"
                aria-selected={opt === selected}
                onClick={() => {
                  applyValue(opt);
                  setOpen(false);
                }}
              >
                <span>{opt}</span>
                {opt === selected ? (
                  <span className="market-quote-select__check" aria-hidden="true">
                    <i className="ri-check-line" aria-hidden="true" />
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {mobileModal}
    </div>
  );
}
